import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, Clock, User, Mail } from 'lucide-react';

interface PendingVerification {
  id: string;
  user_id: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  created_at: string;
  user_profile?: {
    first_name?: string;
    last_name?: string;
    email: string;
    organization_name?: string;
  };
}

interface PendingVerificationsCardProps {
  providerId: string;
}

export const PendingVerificationsCard: React.FC<PendingVerificationsCardProps> = ({ 
  providerId 
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedVerificationId, setSelectedVerificationId] = useState<string | null>(null);

  // Fetch pending verifications for this provider
  const { data: verifications, isLoading } = useQuery({
    queryKey: ['pending-verifications', providerId],
    queryFn: async () => {
      // Get pending verifications specifically for this provider
      const { data: repsData, error: repsError } = await supabase
        .from('peo_representatives')
        .select(`
          id,
          user_id,
          verification_status,
          created_at
        `)
        .eq('provider_id', providerId)
        .eq('verification_status', 'pending');

      if (repsError) {
        console.error('Error fetching pending verifications:', repsError);
        throw repsError;
      }

      if (!repsData || repsData.length === 0) {
        return [];
      }

      // Get user profiles for these pending verifications
      const userIds = repsData.map(rep => rep.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, organization_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching user profiles:', profilesError);
        throw profilesError;
      }

      // Combine the data
      const verifications: PendingVerification[] = repsData.map(rep => {
        const profile = profilesData?.find(p => p.id === rep.user_id);
        return {
          id: rep.id,
          user_id: rep.user_id,
          verification_status: rep.verification_status as 'pending' | 'verified' | 'rejected',
          created_at: rep.created_at,
          user_profile: profile ? {
            first_name: profile.first_name,
            last_name: profile.last_name,
            email: profile.email,
            organization_name: profile.organization_name
          } : undefined
        };
      });

      return verifications;
    }
  });

  // Approve verification mutation
  const approveVerification = useMutation({
    mutationFn: async (verificationId: string) => {
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        throw new Error('Authentication required');
      }

      const { error } = await supabase
        .from('peo_representatives')
        .update({
          verification_status: 'verified',
          verified_by: session.user.id,
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId);

      if (error) {
        throw new Error(`Failed to approve verification: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-verifications', providerId] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats', providerId] });
      toast({
        title: "Verification Approved",
        description: "Representative has been verified successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to approve verification.",
        variant: "destructive"
      });
    }
  });

  // Reject verification mutation
  const rejectVerification = useMutation({
    mutationFn: async ({ verificationId, reason }: { verificationId: string; reason: string }) => {
      await supabase.auth.refreshSession();
      
      const { error } = await supabase
        .from('peo_representatives')
        .update({
          verification_status: 'rejected',
          rejection_reason: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', verificationId);

      if (error) {
        throw new Error(`Failed to reject verification: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-verifications', providerId] });
      queryClient.invalidateQueries({ queryKey: ['organization-stats', providerId] });
      toast({
        title: "Verification Rejected",
        description: "Representative verification has been rejected."
      });
      setRejectionReason('');
      setSelectedVerificationId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reject verification.",
        variant: "destructive"
      });
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Verifications
          </CardTitle>
          <CardDescription>Loading pending verification requests...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!verifications || verifications.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Verifications
          </CardTitle>
          <CardDescription>No pending verification requests</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            All verification requests have been processed.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Pending Verifications
          <Badge variant="secondary">{verifications.length}</Badge>
        </CardTitle>
        <CardDescription>Review and approve new team member requests</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {verifications.map((verification) => (
          <div key={verification.id} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <div className="font-medium">
                  {verification.user_profile?.first_name} {verification.user_profile?.last_name}
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {verification.user_profile?.email}
                </div>
                {verification.user_profile?.organization_name && (
                  <div className="text-sm text-muted-foreground">
                    {verification.user_profile.organization_name}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  Requested: {new Date(verification.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => approveVerification.mutate(verification.id)}
                disabled={approveVerification.isPending}
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rejectVerification.isPending}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setSelectedVerificationId(verification.id)}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reject Verification Request</AlertDialogTitle>
                    <AlertDialogDescription>
                      Please provide a reason for rejecting this verification request.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Reason for rejection..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => {
                      setRejectionReason('');
                      setSelectedVerificationId(null);
                    }}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        if (selectedVerificationId && rejectionReason.trim()) {
                          rejectVerification.mutate({
                            verificationId: selectedVerificationId,
                            reason: rejectionReason.trim()
                          });
                        }
                      }}
                      disabled={!rejectionReason.trim()}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Reject Request
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};